// Fila persistente de sincronização — resolve "dados que sumiam quando save
// falhava silenciosamente". Bianca 31/07/26.
//
// Fluxo:
//   1. Toda escrita passa por enqueue() ANTES de tentar salvar no server
//   2. Se save OK → remove(id)
//   3. Se save ERRO → item fica na fila, tenta de novo em 30s (ou no próximo mount)
//   4. Fila persiste em localStorage entre reloads → nunca perde dados
//   5. Header mostra "🟡 N pendentes" enquanto tiver item na fila
//
// Uso:
//   import { enqueue, remove, flush, getPending, subscribe } from '@/lib/syncQueue'

const KEY = 'kd_sync_queue_v1'
const listeners = new Set()

function read() {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list))
  } catch (e) {
    console.error('[syncQueue] localStorage cheio ou bloqueado:', e)
  }
  listeners.forEach(cb => {
    try { cb(list) } catch { /* ignore listener errors */ }
  })
}

// Adiciona/atualiza um item na fila. Se já existe (mesmo campo+id), substitui.
// Retorna o ID interno pra permitir remoção posterior.
//
// `remocoes` = ids que a pessoa EXCLUIU de fato nesta aba (lista simples:
// array de ids; mapa por empresa: { empresa: [ids] }). É a única fonte de
// exclusão aceita no save por item — ver incidente de 14/09/2026 em App.jsx.
// Ao substituir um pendente do mesmo campo, as remoções dele são somadas, senão
// uma exclusão ainda não gravada se perderia.
export function enqueue(campo, valor, remocoes) {
  const list = read()
  const id = `${campo}::${Date.now()}::${Math.random().toString(36).slice(2, 8)}`
  const anterior = list.find(e => e.campo === campo)
  const entry = {
    id,
    campo,
    valor,
    remocoes: somarRemocoes(anterior?.remocoes, remocoes),
    ts: Date.now(),
    tentativas: 0,
    ultimoErro: null,
  }
  // Se já tem um enqueue pendente do MESMO campo, remove o antigo (mais novo ganha)
  const filtered = list.filter(e => e.campo !== campo)
  filtered.push(entry)
  write(filtered)
  return id
}

export function somarRemocoes(a, b) {
  if (!a) return b || null
  if (!b) return a
  if (Array.isArray(a) && Array.isArray(b)) return [...new Set([...a, ...b].map(String))]
  if (!Array.isArray(a) && !Array.isArray(b)) {
    const out = { ...a }
    for (const [empresa, ids] of Object.entries(b)) {
      out[empresa] = [...new Set([...(out[empresa] || []), ...ids].map(String))]
    }
    return out
  }
  return b
}

export function remove(id) {
  const list = read()
  const filtered = list.filter(e => e.id !== id)
  if (filtered.length !== list.length) write(filtered)
}

export function marcarErro(id, mensagem) {
  const list = read()
  const idx = list.findIndex(e => e.id === id)
  if (idx === -1) return
  list[idx] = { ...list[idx], tentativas: list[idx].tentativas + 1, ultimoErro: String(mensagem || '') }
  write(list)
}

export function getPending() {
  return read()
}

export function subscribe(cb) {
  listeners.add(cb)
  // Notifica com estado atual imediatamente
  try { cb(read()) } catch { /* ignore */ }
  return () => listeners.delete(cb)
}

// Recusa da trava contra apagamento em massa (trg_proteger_remocoes, mig 021).
// Gravação recusada vem de uma tela desatualizada: reenviar não adianta e só
// repete o aviso a cada 30 s, então ela sai da fila em vez de ser retentada.
export function ehBloqueioDeRemocao(e) {
  return e?.code === 'P0001' || /Grava\S{1,4}o bloqueada/i.test(e?.message || '')
}

// Chama a função de save de cada item pendente.
// saveFn = async (campo, valor, { remocoes, ts }) => void
// Se saveFn resolve, remove. Se rejeita, marca erro e mantém — exceto
// recusa da trava de remoção, que é descartada.
export async function flush(saveFn) {
  const list = read()
  if (list.length === 0) return { ok: 0, fail: 0, descartadas: 0 }
  let ok = 0, fail = 0, descartadas = 0
  for (const entry of list) {
    try {
      await saveFn(entry.campo, entry.valor, { remocoes: entry.remocoes || null, ts: entry.ts })
      remove(entry.id)
      ok++
    } catch (e) {
      if (ehBloqueioDeRemocao(e)) { remove(entry.id); descartadas++; continue }
      marcarErro(entry.id, e?.message || 'erro desconhecido')
      fail++
    }
  }
  return { ok, fail, descartadas }
}

export function limparTudo() {
  write([])
}
